import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  AppBar,
  Box,
  Button,
  Container,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
  ThemeProvider,
  useScrollTrigger,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { BrowserRouter as Router, Link, Route, Routes, useLocation } from 'react-router-dom';
import LandingPage from './LandingPage';
import DemoPage from './DemoPage';
import Login from './Login';
import Register from './Register';
import Dashboard from './Dashboard/Dashboard';
import TeacherConsole from './TeacherConsole';
import { UserContext } from './UserContext';
import InfoPage from './InfoPage';
import SetupGuide from './SetupGuide';
import Contact from './Contact';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfService from './TermsOfService';
import AboutUs from './AboutUs';
import SubscriptionManagement from './SubscriptionManagement';
import AdminPage from './AdminPage';
import InviteAcceptPage from './InviteAcceptPage';
import favicon from './images/favicon.png';
import theme, { FONT_DISPLAY, INK, TEAL } from './theme';

const footerExploreLinks = [
  { label: 'Product', to: '/info' },
  { label: 'Live demo', to: '/demo' },
  { label: 'Rollout guide', to: '/setup' },
  { label: 'Pricing', to: '/subscription' },
];

const footerCompanyLinks = [
  { label: 'About', to: '/about' },
  { label: 'Contact', to: '/contact' },
  { label: 'Privacy policy', to: '/privacy' },
  { label: 'Terms of service', to: '/terms' },
];

function BrandMark({ compact = false }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
      <Box
        sx={{
          width: compact ? 30 : 36,
          height: compact ? 30 : 36,
          borderRadius: compact ? '8px' : '10px',
          overflow: 'hidden',
          flexShrink: 0,
          boxShadow: '0 2px 6px rgba(11,46,67,0.18)',
        }}
      >
        <img src={favicon} alt="BlockWebAI logo" style={{ width: '100%', height: '100%', display: 'block' }} />
      </Box>
      <Box sx={{ minWidth: 0, lineHeight: 1 }}>
        <Typography
          component="span"
          sx={{
            display: 'block',
            fontFamily: FONT_DISPLAY,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            fontSize: compact ? '0.98rem' : '1.06rem',
            color: INK,
            whiteSpace: 'nowrap',
          }}
        >
          BlockWebAI
        </Typography>
        {!compact && (
          <Typography
            component="span"
            sx={{
              display: { xs: 'none', sm: 'block' },
              fontSize: '0.66rem',
              fontWeight: 800,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: TEAL,
              mt: 0.3,
            }}
          >
            For Schools
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // 'instant' bypasses the html scroll-behavior:smooth so route changes don't animate.
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);

  return null;
}

function NotFound() {
  return (
    <Container sx={{ py: { xs: 8, md: 12 }, textAlign: 'center' }}>
      <Typography
        sx={{
          fontFamily: FONT_DISPLAY,
          fontWeight: 800,
          fontSize: { xs: '4.5rem', md: '6rem' },
          lineHeight: 1,
          background: 'linear-gradient(120deg, #0E4D6E 0%, #13947F 100%)',
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          color: 'transparent',
        }}
      >
        404
      </Typography>
      <Typography variant="h3" sx={{ mt: 2, color: INK, fontSize: { xs: '1.5rem', md: '1.9rem' } }}>
        This page isn't on the map.
      </Typography>
      <Typography sx={{ mt: 1.5, color: '#52677A', maxWidth: 460, mx: 'auto', lineHeight: 1.7 }}>
        The link may be old or mistyped. The homepage and the live demo are the fastest ways back to the product.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center" sx={{ mt: 4 }}>
        <Button variant="contained" color="primary" component={Link} to="/">
          Go to the Homepage
        </Button>
        <Button variant="outlined" component={Link} to="/demo">
          Try the Live Demo
        </Button>
      </Stack>
    </Container>
  );
}

function SiteFooter() {
  return (
    <Box
      component="footer"
      sx={{
        mt: 8,
        background:
          'radial-gradient(circle at 90% -20%, rgba(19,148,127,0.25), transparent 44%), radial-gradient(circle at -10% 120%, rgba(14,77,110,0.5), transparent 52%), linear-gradient(165deg, #0A2838 0%, #0E3A54 100%)',
        color: 'rgba(255,255,255,0.78)',
      }}
    >
      <Container sx={{ py: { xs: 6, md: 8 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1.4fr 0.8fr 0.8fr 1fr' },
            gap: { xs: 4, md: 5 },
          }}
        >
          <Box>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box sx={{ width: 36, height: 36, borderRadius: '10px', overflow: 'hidden', flexShrink: 0 }}>
                <img src={favicon} alt="BlockWebAI logo" style={{ width: '100%', height: '100%', display: 'block' }} />
              </Box>
              <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: '1.1rem', color: 'white', letterSpacing: '-0.02em' }}>
                BlockWebAI for Schools
              </Typography>
            </Stack>
            <Typography sx={{ mt: 2, maxWidth: 340, fontSize: '0.94rem', lineHeight: 1.75, color: 'rgba(255,255,255,0.6)' }}>
              Page-level filtering for schools that need more precision than blunt category blocks. Keep the lesson open — block exactly what does not belong.
            </Typography>
            <Stack direction="row" spacing={1.25} sx={{ mt: 3 }}>
              <Button
                variant="contained"
                color="secondary"
                size="small"
                component={Link}
                to="/register"
                sx={{ px: 2 }}
              >
                Start a Pilot
              </Button>
              <Button
                variant="outlined"
                size="small"
                component={Link}
                to="/demo"
                sx={{
                  px: 2,
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.3)',
                  '&:hover': { borderColor: 'rgba(255,255,255,0.6)', bgcolor: 'rgba(255,255,255,0.06)' },
                }}
              >
                Try the Demo
              </Button>
            </Stack>
          </Box>

          <Box>
            <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '0.74rem', letterSpacing: '0.14em', textTransform: 'uppercase', mb: 2 }}>
              Explore
            </Typography>
            <Stack spacing={1.25}>
              {footerExploreLinks.map((item) => (
                <Box
                  key={item.to}
                  component={Link}
                  to={item.to}
                  sx={{
                    color: 'rgba(255,255,255,0.66)',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: '0.92rem',
                    width: 'fit-content',
                    transition: 'color 140ms ease',
                    '&:hover': { color: '#7EE8D6' },
                  }}
                >
                  {item.label}
                </Box>
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '0.74rem', letterSpacing: '0.14em', textTransform: 'uppercase', mb: 2 }}>
              Company
            </Typography>
            <Stack spacing={1.25}>
              {footerCompanyLinks.map((item) => (
                <Box
                  key={item.to}
                  component={Link}
                  to={item.to}
                  sx={{
                    color: 'rgba(255,255,255,0.66)',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: '0.92rem',
                    width: 'fit-content',
                    transition: 'color 140ms ease',
                    '&:hover': { color: '#7EE8D6' },
                  }}
                >
                  {item.label}
                </Box>
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '0.74rem', letterSpacing: '0.14em', textTransform: 'uppercase', mb: 2 }}>
              Pilot support
            </Typography>
            <Typography sx={{ fontSize: '0.92rem', lineHeight: 1.75, color: 'rgba(255,255,255,0.6)' }}>
              Questions about deployment, procurement, or policy design? We answer directly.
            </Typography>
            <Box
              component="a"
              href="mailto:jonathanlarsonbusiness@gmail.com"
              sx={{
                display: 'inline-block',
                mt: 1.5,
                color: '#7EE8D6',
                fontWeight: 700,
                fontSize: '0.92rem',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              jonathanlarsonbusiness@gmail.com
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.1)' }} />

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
        >
          <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.45)' }}>
            © {new Date().getFullYear()} BlockWebAI. Built for learning environments.
          </Typography>
          <Stack direction="row" spacing={2.5}>
            <Box component={Link} to="/privacy" sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.82rem', textDecoration: 'none', '&:hover': { color: 'white' } }}>
              Privacy
            </Box>
            <Box component={Link} to="/terms" sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.82rem', textDecoration: 'none', '&:hover': { color: 'white' } }}>
              Terms
            </Box>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}

function AppShell() {
  const { user, setUser } = useContext(UserContext);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const location = useLocation();
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 8 });
  const isViewerUser = user?.organization_role === 'viewer';
  const isConsoleRoute = Boolean(user) && ['/', '/teacher', '/admin'].includes(location.pathname);

  const publicLinks = useMemo(
    () => [
      { label: 'Demo', to: '/demo' },
      { label: 'Product', to: '/info' },
      { label: 'Rollout', to: '/setup' },
      { label: 'Pricing', to: '/subscription' },
      { label: 'About', to: '/about' },
      { label: 'Contact', to: '/contact' },
    ],
    []
  );

  const signedInLinks = useMemo(() => {
    if (isViewerUser) {
      return [
        { label: 'Teacher Console', to: '/' },
        { label: 'Contact', to: '/contact' },
      ];
    }

    const links = [
      { label: 'Dashboard', to: '/' },
      { label: 'Demo', to: '/demo' },
      { label: 'Product', to: '/info' },
      { label: 'Rollout', to: '/setup' },
      { label: 'Pricing', to: '/subscription' },
      { label: 'Contact', to: '/contact' },
    ];

    if (user?.is_admin) {
      links.push({ label: 'Admin', to: '/admin' });
    }

    return links;
  }, [isViewerUser, user]);

  const navLinks = isConsoleRoute ? [] : user ? signedInLinks : publicLinks;
  const handleLogout = () => {
    setLogoutOpen(false);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common.Authorization;
    window.location.href = '/';
  };

  const navButtonSx = (to) => ({
    color: location.pathname === to ? '#0B2E43' : '#3D5A6E',
    fontWeight: 650,
    fontSize: '0.92rem',
    borderRadius: '999px',
    px: 1.6,
    py: 0.7,
    minHeight: 0,
    backgroundColor: location.pathname === to ? 'rgba(19,148,127,0.12)' : 'transparent',
    '&:hover': {
      backgroundColor: location.pathname === to ? 'rgba(19,148,127,0.16)' : 'rgba(11,46,67,0.06)',
    },
  });

  const drawerLinks = (
    <Box sx={{ width: 300, p: 2.5, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 0.5, pb: 2 }}>
        <BrandMark />
        <IconButton onClick={() => setMobileMenuOpen(false)} aria-label="Close menu" size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Divider sx={{ mb: 1.5 }} />
      <List sx={{ flex: 1 }}>
        {navLinks.map((link) => (
          <ListItemButton
            key={link.to}
            component={Link}
            to={link.to}
            selected={location.pathname === link.to}
            onClick={() => setMobileMenuOpen(false)}
            sx={{
              borderRadius: '10px',
              mb: 0.5,
              '&.Mui-selected': {
                bgcolor: 'rgba(19,148,127,0.12)',
                '&:hover': { bgcolor: 'rgba(19,148,127,0.16)' },
              },
            }}
          >
            <ListItemText
              primary={link.label}
              primaryTypographyProps={{ fontWeight: location.pathname === link.to ? 750 : 600 }}
            />
          </ListItemButton>
        ))}
      </List>
      <Divider sx={{ my: 1.5 }} />
      {user ? (
        <Button fullWidth variant="contained" color="primary" onClick={() => setLogoutOpen(true)}>
          Sign Out
        </Button>
      ) : (
        <Box sx={{ display: 'grid', gap: 1 }}>
          <Button
            fullWidth
            variant="contained"
            component={Link}
            to="/register"
            onClick={() => setMobileMenuOpen(false)}
            sx={{
              color: 'white',
              background: 'linear-gradient(135deg, #0E4D6E 0%, #13947F 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #0C4361 0%, #108971 100%)' },
            }}
          >
            Start School Pilot
          </Button>
          <Button fullWidth variant="outlined" component={Link} to="/login" onClick={() => setMobileMenuOpen(false)}>
            Sign In
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: isConsoleRoute ? '#F0F4F7' : '#F3F6F8',
        overflowX: 'hidden',
      }}
    >
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: isConsoleRoute ? '#ffffff' : 'rgba(255, 255, 255, 0.82)',
          borderBottom: '1px solid',
          borderColor: scrolled || isConsoleRoute ? '#DFE8EE' : 'rgba(223,232,238,0.4)',
          backdropFilter: isConsoleRoute ? 'none' : 'blur(16px) saturate(1.4)',
          boxShadow: !isConsoleRoute && scrolled ? '0 8px 24px -16px rgba(11,46,67,0.18)' : 'none',
          color: '#13293A',
          transition: 'box-shadow 200ms ease, border-color 200ms ease',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 60, md: isConsoleRoute ? 56 : 68 }, px: { xs: 2, md: isConsoleRoute ? 2.5 : 4 } }}>
          <Box
            component={Link}
            to="/"
            aria-label="BlockWebAI home"
            sx={{ textDecoration: 'none', color: 'inherit', flexGrow: 1, display: 'flex', alignItems: 'center' }}
          >
            <BrandMark compact={isConsoleRoute} />
          </Box>

          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
            {navLinks.map((link) => (
              <Button key={link.to} component={Link} to={link.to} sx={navButtonSx(link.to)}>
                {link.label}
              </Button>
            ))}
            {user ? (
              <Button
                variant="outlined"
                color="primary"
                sx={{ ml: navLinks.length ? 1.5 : 0, minHeight: 36, px: 2 }}
                onClick={() => setLogoutOpen(true)}
              >
                Sign Out
              </Button>
            ) : (
              <>
                <Button component={Link} to="/login" sx={navButtonSx('/login')}>
                  Sign In
                </Button>
                <Button
                  variant="contained"
                  component={Link}
                  to="/register"
                  sx={{
                    ml: 1.25,
                    minHeight: 38,
                    px: 2.25,
                    color: 'white',
                    background: 'linear-gradient(135deg, #0E4D6E 0%, #13947F 100%)',
                    boxShadow: '0 10px 24px -10px rgba(14,77,110,0.55)',
                    transition: 'transform 160ms ease, box-shadow 160ms ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #0C4361 0%, #108971 100%)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 14px 28px -10px rgba(14,77,110,0.65)',
                    },
                  }}
                >
                  Start School Pilot
                </Button>
              </>
            )}
          </Box>

          <IconButton
            sx={{ display: { xs: 'inline-flex', md: 'none' }, ml: 0.5 }}
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <ScrollToTop />

      <Box component="main" sx={{ flex: 1 }}>
        <Routes>
          <Route path="/" element={user ? (isViewerUser ? <TeacherConsole /> : <Dashboard />) : <LandingPage />} />
          <Route path="/teacher" element={user ? <TeacherConsole /> : <Login />} />
          <Route path="/demo" element={<DemoPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/subscription" element={<SubscriptionManagement />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/info" element={<InfoPage />} />
          <Route path="/setup" element={<SetupGuide />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/invite/:token" element={<InviteAcceptPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Box>

      {!isConsoleRoute && <SiteFooter />}

      <Drawer anchor="right" open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)}>
        {drawerLinks}
      </Drawer>

      <Dialog open={logoutOpen} onClose={() => setLogoutOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Sign out?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This ends the current administrator session on this browser.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setLogoutOpen(false)}>Cancel</Button>
          <Button onClick={handleLogout} variant="contained" color="primary">
            Sign Out
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AppShell />
      </Router>
    </ThemeProvider>
  );
}

export default App;
